import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical } from 'lucide-react'

export type ActionItem = {
  label: string
  icon: React.ReactNode
  onClick: () => void
  disabled?: boolean
  /** Shown under a disabled item to say why it can't be used. */
  hint?: string
  danger?: boolean
}

const MENU_WIDTH = 208 // w-52

/**
 * Row-actions "⋮" menu — collapses a row of icon buttons into a single dropdown.
 * The panel renders through a portal into document.body, positioned by the trigger
 * button's own coordinates, so it isn't clipped by a scrollable table ancestor
 * (e.g. the horizontal-scroll wrapper on desktop tables).
 */
export function ActionsMenu({ items }: { items: ActionItem[] }) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const openMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (rect) setPosition({ top: rect.bottom + 4, left: rect.right - MENU_WIDTH })
    setOpen(o => !o)
  }

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) setOpen(false)
    }
    const handleScrollOrResize = () => setOpen(false)
    document.addEventListener('mousedown', handleClick)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [open])

  return (
    <>
      <button
        ref={buttonRef}
        onClick={openMenu}
        title="Actions"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
      >
        <MoreVertical size={15} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: position.top, left: position.left, width: MENU_WIDTH }}
          className="z-50 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden py-1"
        >
          {items.map(item => (
            <button
              key={item.label}
              onClick={() => { setOpen(false); item.onClick() }}
              disabled={item.disabled}
              title={item.disabled ? item.hint : undefined}
              className={`w-full flex items-start gap-2.5 px-3 py-2 text-sm text-left cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="mt-0.5 shrink-0">{item.icon}</span>
              <span>
                {item.label}
                {item.disabled && item.hint && <span className="block text-[11px] leading-snug text-gray-400 font-normal">{item.hint}</span>}
              </span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}
